import React from 'react';
import { Container, Box, Typography, Paper, Divider, List, ListItem, ListItemText, Link, Chip, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { APP_NAME } from '../../config/branding';

export default function Terms() {
  const { t } = useTranslation();
  return (
    <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
      <Stack spacing={3} sx={{ mb: 4 }}>
        <Chip color="primary" label={t('legal_terms.chip')} sx={{ alignSelf: 'flex-start' }} />
        <Typography variant="h3" component="h1">
          {t('legal_terms.title', { appName: APP_NAME })}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('legal_terms.last_updated')}: {new Date().toLocaleDateString()}
        </Typography>
      </Stack>

      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 2, bgcolor: 'background.paper' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="body1" paragraph>
            {t('legal_terms.intro', { appName: APP_NAME })}
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { md: '320px 1fr' }, gap: { xs: 2, md: 4 } }}>
          <Box sx={{ position: { md: 'sticky' }, top: { md: 88 }, alignSelf: 'start' }}>
            <Typography variant="overline" color="text.secondary">{t('legal_terms.summary')}</Typography>
            <List dense>
              <ListItem component="a" href="#objet" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.purpose')} />
              </ListItem>
              <ListItem component="a" href="#definitions" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.definitions')} />
              </ListItem>
              <ListItem component="a" href="#compte" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.account')} />
              </ListItem>
              <ListItem component="a" href="#acces" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.access')} />
              </ListItem>
              <ListItem component="a" href="#acceptable" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.acceptable_use')} />
              </ListItem>
              <ListItem component="a" href="#paiement" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.pricing')} />
              </ListItem>
              <ListItem component="a" href="#propriete" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.ip')} />
              </ListItem>
              <ListItem component="a" href="#responsabilite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.liability')} />
              </ListItem>
              <ListItem component="a" href="#resiliation" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.termination')} />
              </ListItem>
              <ListItem component="a" href="#modifications" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.modifications')} />
              </ListItem>
              <ListItem component="a" href="#droit" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.law')} />
              </ListItem>
              <ListItem component="a" href="#contact" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.contact')} />
              </ListItem>
              <ListItem component="a" href="#infos-complementaires" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.additional_info')} />
              </ListItem>
              <ListItem component="a" href="#avis-contenu" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.reviews')} />
              </ListItem>
              <ListItem component="a" href="#indemnisation" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.indemnity')} />
              </ListItem>
              <ListItem component="a" href="#divisibilite" sx={{ color: 'text.secondary' }}>
                <ListItemText primary={t('legal_terms.sections.severability')} />
              </ListItem>
            </List>
          </Box>

          <Box>
            <Box id="objet" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.purpose_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.purpose_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="definitions" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.definitions_title')}</Typography>
              <Typography variant="body2" color="text.secondary" paragraph>{t('legal_terms.content.definitions_intro')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={<>
                    <strong>{t('legal_terms.content.client_term')}</strong> : {t('legal_terms.content.client_def', { appName: APP_NAME })}
                  </>} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={<>
                    <strong>{t('legal_terms.content.pro_term')}</strong> : {t('legal_terms.content.pro_def', { appName: APP_NAME })}
                  </>} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={<>
                    <strong>{t('legal_terms.content.account_term')}</strong> : {t('legal_terms.content.account_def')}
                  </>} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="compte" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>3. Compte & sécurité</Typography>
              <Typography variant="body1" paragraph>
                La création d'un compte est obligatoire pour accéder aux fonctionnalités de réservation. Vous êtes responsable 
                de la confidentialité de vos identifiants et de toute activité réalisée depuis votre compte.
              </Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary="Vérification de compte: nous pouvons vous demander de confirmer votre email et/ou téléphone." />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary="Sécurité renforcée: activez lauthentification multifacteurs lorsque disponible." />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary="Mise à jour: vous vous engagez à maintenir vos informations exactes et à jour." />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary">
                Pour protéger votre compte, utilisez un mot de passe unique et évitez dutiliser des réseaux non sécurisés.
              </Typography>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.account_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.account_verify')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.account_mfa')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.account_update')} />
                </ListItem>
              </List>
              <Typography variant="body2" color="text.secondary">
                {t('legal_terms.content.account_security')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="acces" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.access_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.access_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="acceptable" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.acceptable_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.acceptable_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.acceptable_2')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.acceptable_3')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.acceptable_4')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.acceptable_5')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.acceptable_6')} />
                </ListItem>
              </List>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="paiement" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>6. Tarifs et paiement</Typography>
              <Typography variant="body1" paragraph>
                Les tarifs des prestations sont fixés par les professionnels partenaires. {APP_NAME} peut percevoir des frais de service 
                lors de la réservation.
              </Typography>
              <Typography variant="body1" paragraph>
                En cas dannulation tardive ou de nonprésentation, des frais peuvent être appliqués conformément aux règles du professionnel.
                Les taxes applicables et frais de service sont précisés lors de la confirmation.
              </Typography>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.pricing_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.pricing_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="propriete" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>7. Propriété intellectuelle</Typography>
              <Typography variant="body1" paragraph>
                {APP_NAME} et son contenu (logos, textes, images, design) sont protégés par le droit de la propriété intellectuelle.
                Une licence personnelle, non exclusive et non transférable vous est accordée pour utiliser {APP_NAME} conformément aux CGU. Toute
                utilisation commerciale non autorisée est prohibée.
              </Typography>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.ip_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.ip_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="responsabilite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>8. Responsabilités</Typography>
              <Typography variant="body1" paragraph>
                Les professionnels partenaires sont responsables de la qualité des prestations fournies. {APP_NAME} agit comme intermédiaire 
                et ne peut être tenu responsable des litiges liés à l'exécution des services.
              </Typography>
              <Typography variant="body1" paragraph>
                Dans la limite permise par la loi, {APP_NAME} nest pas responsable des pertes indirectes, du manque à gagner, ni des dommages
                résultant d'interruptions du service, de maintenances ou de cas de force majeure.
              </Typography>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.liability_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.liability_text', { appName: APP_NAME })}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="resiliation" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>9. Résiliation</Typography>
              <Typography variant="body1" paragraph>
                Vous pouvez résilier votre compte à tout moment via les paramètres de votre compte ou en nous contactant.
                La résiliation peut entraîner la suppression ou lanonymisation de certaines données, sous réserve des obligations légales de
                conservation. Les réservations en cours restent soumises aux politiques du professionnel.
              </Typography>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.termination_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.termination_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="modifications" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>10. Modifications des CGU</Typography>
              <Typography variant="body2" color="text.secondary">
                Nous vous recommandons de consulter régulièrement cette page pour prendre connaissance des dernières versions.
              </Typography>
              <Typography variant="body1" paragraph>
                Les modifications majeures seront notifiées aux utilisateurs par email ou via l'application. 
                L'utilisation continue du service après modification vaut acceptation des nouvelles CGU.
              </Typography>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.modifications_title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('legal_terms.content.modifications_text')}
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="droit" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>11. Loi applicable</Typography>
              <Typography variant="body1" paragraph>
                Les présentes CGU sont régies par le droit français. En cas de litige, vous pouvez recourir gratuitement à un médiateur de la consommation 
                ou utiliser la plateforme européenne de règlement des litiges en ligne
                (<Link href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener">ODR</Link>).
              </Typography>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.law_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.law_text')} (<Link href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener">ODR</Link>).
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Box id="contact">
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.contact_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.contact_text')} <Link href="mailto:contact@saloone.fr">contact@saloone.fr</Link> {t('legal_terms.content.contact_or')}{' '}
                <Link href="/legal/confidentialite">{t('legal_terms.content.privacy_policy')}</Link>.
              </Typography>
            </Box>            <Divider sx={{ my: 3 }} />
            <Box id="infos-complementaires" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.additional_info_title')}</Typography>
              <List sx={{ pl: 2 }}>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.additional_1')} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.additional_2', { appName: APP_NAME })} />
                </ListItem>
                <ListItem sx={{ display: 'list-item', pl: 0 }}>
                  <ListItemText primary={t('legal_terms.content.additional_3')} />
                </ListItem>
              </List>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="avis-contenu" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.reviews_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.reviews_text')}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="indemnisation" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.indemnity_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.indemnity_text', { appName: APP_NAME })}
              </Typography>
            </Box>
            <Divider sx={{ my: 3 }} />
            <Box id="divisibilite" sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom>{t('legal_terms.content.severability_title')}</Typography>
              <Typography variant="body1" paragraph>
                {t('legal_terms.content.severability_text')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
}





